<script>
import { isEqual } from 'lodash'
import { mapActions } from 'vuex'
import {
  getEditableLocations,
  buildClientPreferenceChange,
  sortClientsWithUnpreferredRestrictionsLast,
} from '@helpers/candidate-preference-helpers'
import CandidatePreferenceClientItem from '@/components/candidate/candidate-preference-client-item.vue'
import CandidatePreferenceListSkeleton from '@/components/candidate/candidate-preference-list-skeleton.vue'
import HeightTransition from '@/components/general/height-transition.vue'

export default {
  name: 'CandidatePreferenceModal',
  components: {
    CandidatePreferenceClientItem,
    CandidatePreferenceListSkeleton,
    HeightTransition,
  },
  model: {
    prop: 'show',
    event: 'input',
  },
  props: {
    candidateId: {
      type: Number,
      required: true,
    },
    candidateName: {
      type: String,
      default: '',
    },
    preferredLocationCount: {
      type: Number,
      default: 0,
    },
    candidateAccessibleLocationCount: {
      type: Number,
      default: 0,
    },
    clientIds: {
      type: Array,
      required: true,
    },
    availableClientCount: {
      type: Number,
      default: 0,
    },
    show: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      details: null,
      loading: false,
      saving: false,
      loadError: null,
      saveError: null,
      conflictError: false,
      initialClientPreferences: [],
      expandedClientIds: [],
    }
  },
  computed: {
    isOpen: {
      get() {
        return this.show
      },
      set(value) {
        this.$emit('input', value)
      },
    },
    preferenceChanges() {
      if (this.loading) return []

      return (this.details?.clients ?? [])
        .map((client) => buildClientPreferenceChange(client))
        .filter((change) => {
          const initialPreference = this.initialClientPreferences.find(
            (preference) => preference.clientId === change.clientId
          )

          return !isEqual(change, initialPreference)
        })
    },
    isDirty() {
      return this.preferenceChanges.length > 0
    },
    hasDetails() {
      return (this.details?.clients?.length ?? 0) > 0
    },
    sortedClients() {
      return sortClientsWithUnpreferredRestrictionsLast(
        this.details?.clients ?? []
      )
    },
    // Once details load, if every client is locked there is nothing this contact can do here.
    showLockedSaveTooltip() {
      return (
        !this.loading &&
        this.hasDetails &&
        this.details.clients.every((client) => client.isDisabled)
      )
    },
    preferredSummary() {
      const preferredLocationCount =
        this.details?.preferredLocationCount ?? this.preferredLocationCount

      const accessibleLocationCount =
        this.details?.accessibleLocationCount ??
        this.candidateAccessibleLocationCount

      if (preferredLocationCount === 0) {
        return this.$t('candidateList.card.notCurrentlyPreferred')
      }

      if (
        accessibleLocationCount > 0 &&
        preferredLocationCount === accessibleLocationCount
      ) {
        return this.$t('candidateList.card.preferredAtAllLocations', {
          count: accessibleLocationCount,
        })
      }

      return this.$t('candidateList.card.preferredAtLocations', {
        preferred: preferredLocationCount,
        total: accessibleLocationCount,
      })
    },
    alertMessage() {
      if (this.loadError)
        return this.$t('candidateList.preferenceModal.loadError')

      if (this.conflictError)
        return this.$t('candidateList.preferenceModal.conflictError')

      if (this.saveError)
        return this.$t('candidateList.preferenceModal.saveError')

      if (this.isDirty)
        return this.$t('candidateList.preferenceModal.sharedPreference')

      return null
    },
    listMaxHeight() {
      const { xlOnly, lgAndUp, mdAndUp } = this.$vuetify.breakpoint

      if (xlOnly) return '600px'
      if (lgAndUp) return '460px'
      if (mdAndUp) return '360px'

      return '280px'
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(value) {
        if (value) this.loadDetails()
      },
    },
  },
  methods: {
    ...mapActions({
      getCandidatePreferredDetails: 'candidate/getCandidatePreferredDetails',
      updateCandidatePreferred: 'candidate/setCandidatePreferred',
    }),
    async loadDetails() {
      this.loading = true
      this.loadError = null
      this.saveError = null
      this.conflictError = false
      this.initialClientPreferences = []
      this.expandedClientIds = []

      const { data, error } = await this.getCandidatePreferredDetails({
        candidateId: this.candidateId,
        payload: { clientIds: this.clientIds },
      })

      if (error) {
        this.loadError = error
        this.details = null
        this.loading = false
        return
      }

      this.details = data
      this.initialClientPreferences = (data?.clients ?? []).map((client) =>
        buildClientPreferenceChange(client)
      )
      this.loading = false
    },
    isClientExpanded(clientId) {
      return this.expandedClientIds.includes(clientId)
    },
    toggleClientExpanded(clientId) {
      const index = this.expandedClientIds.indexOf(clientId)

      if (index === -1) {
        this.expandedClientIds.push(clientId)
        return
      }

      this.expandedClientIds.splice(index, 1)
    },
    toggleClient(client) {
      const draftClient = this.details?.clients.find(
        (draftClient) => draftClient.clientId === client.clientId
      )

      if (!draftClient || draftClient.isDisabled) return

      const editableLocations = getEditableLocations(draftClient)
      const shouldPrefer = !editableLocations.every(
        (location) => location.isPreferred
      )

      editableLocations.forEach((location) => {
        location.isPreferred = shouldPrefer
      })
    },
    toggleLocation(client, location) {
      const draftClient = this.details?.clients.find(
        (draftClient) => draftClient.clientId === client.clientId
      )

      if (!draftClient || draftClient.isDisabled) return

      const draftLocation = draftClient.locations.find(
        (draftLocation) => draftLocation.locationId === location.locationId
      )

      if (!draftLocation || draftLocation.isDisabled) return

      draftLocation.isPreferred = !draftLocation.isPreferred
    },
    async save() {
      if (!this.isDirty || this.saving) return

      this.saving = true
      this.saveError = null
      this.conflictError = false
      const result = await this.updateCandidatePreferred({
        candidateId: this.candidateId,
        payload: {
          clientIds: this.clientIds,
          changes: this.preferenceChanges,
        },
      })

      this.saving = false
      if (result.statusCode === 409) {
        await this.loadDetails()
        this.conflictError = !this.loadError
        return
      }

      if (result.error) {
        this.saveError = result.error
        return
      }

      this.$emit('saved', result.data)
      this.isOpen = false
    },
    close() {
      if (this.saving) return
      this.isOpen = false
    },
  },
}
</script>

<template>
  <BaseModal
    v-model="isOpen"
    width="600"
    border-radius="lg"
    persistent
    hide-cancel
    hide-padding
    header-class="pt-lg px-lg pb-sm"
    actions-class="d-flex justify-space-between align-center p-md"
  >
    <template v-slot:header>
      <div class="preference-modal__header d-flex flex-column w-100">
        <div class="d-flex justify-space-between align-start">
          <div class="preference-modal__titles">
            <p class="display-md font-weight-bold mb-sm">
              {{ $t('candidateList.preferenceModal.title') }}
            </p>
            <div class="text-md-bold">
              <span class="text-capitalize">{{
                candidateName.toLowerCase()
              }}</span>
              <span> - {{ preferredSummary }}</span>
            </div>
            <p
              v-if="availableClientCount > 1"
              class="text-sm neutrals-grey-500-text mb-0 mt-3xs"
            >
              {{
                $t('candidateList.preferenceModal.clientSelectionSummary', {
                  selected: new Set(clientIds).size,
                  total: availableClientCount,
                })
              }}
            </p>
          </div>
          <BaseButton
            icon="mdi-close"
            fab
            tertiary
            sm
            hover-color="grey lighten-4"
            class="ml-md"
            :disabled="saving"
            @click="close"
          />
        </div>
        <HeightTransition content-transition="fade-cycle-down" :duration="250">
          <div
            v-if="alertMessage"
            :key="alertMessage"
            class="preference-modal__status pt-sm"
          >
            <v-alert
              :type="
                loadError || conflictError || saveError ? 'error' : 'warning'
              "
              :icon="
                loadError || conflictError || saveError
                  ? 'mdi-alert-circle'
                  : 'mdi-alert'
              "
              text
              dense
              class="preference-modal__alert text-sm mb-0 px-sm rounded-lg word-break-normal overflow-wrap-normal"
              :class="{
                'preference-modal__alert--warning':
                  !loadError && !conflictError && !saveError,
              }"
            >
              {{ alertMessage }}
              <template v-if="loadError" v-slot:append>
                <BaseButton secondary sm @click="loadDetails">
                  {{ $t('candidateList.preferenceModal.retry') }}
                </BaseButton>
              </template>
            </v-alert>
          </div>
        </HeightTransition>
      </div>
    </template>

    <HeightTransition
      class="preference-modal__body"
      content-transition="fade"
      mode="out-in"
    >
      <CandidatePreferenceListSkeleton v-if="loading" key="loading" />

      <div v-else key="content">
        <ul
          v-if="hasDetails"
          class="preference-modal__tree pa-0 ma-0 overflow-x-hidden overflow-y-auto"
          :style="{ maxHeight: listMaxHeight }"
          aria-live="polite"
        >
          <CandidatePreferenceClientItem
            v-for="client in sortedClients"
            :key="`preference-client-${client.clientId}`"
            :client="client"
            :is-saving-preferences="saving"
            :expanded="isClientExpanded(client.clientId)"
            @toggle-client="toggleClient"
            @toggle-location="toggleLocation"
            @toggle-expanded="toggleClientExpanded"
          />
        </ul>

        <v-alert v-else-if="!loadError" text class="mx-lg my-sm">
          {{ $t('candidateList.preferenceModal.empty') }}
        </v-alert>
      </div>
    </HeightTransition>

    <template v-slot:actions>
      <BaseButton
        tertiary
        md
        border-radius="8px"
        hover-color="neutrals-grey-100"
        class="preference-modal__cancel"
        :disabled="saving"
        @click="close"
      >
        {{ $t('general.cancel') }}
      </BaseButton>
      <span
        :title="
          showLockedSaveTooltip
            ? $t('candidateList.preferenceModal.contactConsultant')
            : null
        "
      >
        <v-btn
          min-width="112"
          color="primary"
          height="40"
          elevation="0"
          :loading="saving"
          :disabled="loading || !isDirty"
          @click="save"
        >
          {{ $t('candidateList.preferenceModal.saveChanges') }}
        </v-btn>
      </span>
    </template>
  </BaseModal>
</template>

<style lang="scss" src="@/styles/candidate/candidate-preference.scss" />
